import { apiFetch, apiUpload, BACKEND_URL } from './api';

/** Resolve a media path (e.g. /media/activities/img.jpg) to a full URL. Returns empty string for falsy input. */
export function mediaUrl(path: string | null | undefined): string {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${BACKEND_URL}${path}`;
}
import type {
  ActivityDetail,
  ActivityInvitation,
  ActivityListItem,
  AuditLogEntry,
  CalendarResponse,
  Coordinator,
  LoginResponse,
  MyRegistration,
  MyVolunteerApplication,
  PaginatedResponse,
  Participant,
  ParticipantStats,
  PartnerDetail,
  RegistrationFormData,
  RegistrationResponse,
  StaffUser,
  Team,
  PartnerListItem,
  Sponsor,
  StaffSponsor,
  StaffActivity,
  Tag,
  User,
  UserConsent,
  VolunteerApplication,
  VolunteerStats,
  VolunteerTeam,
} from './types';

export const STAFF_PAGE_SIZE = 25;

function buildStaffListUrl(base: string, params?: { page?: number; search?: string; [key: string]: string | number | undefined }): string {
  const qs = new URLSearchParams();
  qs.set('page_size', String(STAFF_PAGE_SIZE));
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
  }
  return `${base}?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const authService = {
  login(username: string, password: string) {
    return apiFetch<LoginResponse>('/auth/login/', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },

  googleLogin(credential: string) {
    return apiFetch<LoginResponse>('/auth/google/', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });
  },

  facebookLogin(accessToken: string) {
    return apiFetch<LoginResponse>('/auth/facebook/', {
      method: 'POST',
      body: JSON.stringify({ access_token: accessToken }),
    });
  },

  logout() {
    return apiFetch<void>('/auth/logout/', { method: 'POST' });
  },

  me() {
    return apiFetch<User>('/auth/me/');
  },

  requestPasswordReset(email: string) {
    return apiFetch<{ detail: string }>('/auth/password-reset/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  confirmPasswordReset(uid: string, token: string, newPassword: string) {
    return apiFetch<{ detail: string }>('/auth/password-reset/confirm/', {
      method: 'POST',
      body: JSON.stringify({ uid, token, new_password: newPassword }),
    });
  },

  requestMagicLink(email: string) {
    return apiFetch<{ detail: string }>('/auth/magic-link/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  verifyMagicLink(uid: string, token: string) {
    return apiFetch<LoginResponse>('/auth/magic-link/verify/', {
      method: 'POST',
      body: JSON.stringify({ uid, token }),
    });
  },

  changePassword(currentPassword: string | undefined, newPassword: string) {
    const body: Record<string, string> = { new_password: newPassword };
    if (currentPassword) body.current_password = currentPassword;
    return apiFetch<{ token: string; detail: string }>('/auth/change-password/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  updateMe(data: Record<string, unknown>) {
    return apiFetch<User>('/auth/me/', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  acceptTerms(marketingEmailsAccepted: boolean) {
    return apiFetch<{ detail: string }>('/auth/me/accept-terms/', {
      method: 'POST',
      body: JSON.stringify({ marketing_emails_accepted: marketingEmailsAccepted }),
    });
  },

  getConsent() {
    return apiFetch<UserConsent>('/auth/me/consent/');
  },

  updateConsent(data: { marketing_emails_accepted: boolean }) {
    return apiFetch<UserConsent>('/auth/me/consent/', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  exportData() {
    return apiFetch<Record<string, unknown>>('/auth/me/export/');
  },

  getVolunteerRequestStatus() {
    return apiFetch<{ is_volunteer: boolean; request_status: string; rejected_reason: string }>('/auth/me/volunteer-request/');
  },

  requestVolunteerRole() {
    return apiFetch<{ status: string }>('/auth/me/volunteer-request/', { method: 'POST' });
  },

  deleteAccount(confirmation: string) {
    return apiFetch<{ detail: string }>('/auth/me/delete/', {
      method: 'DELETE',
      body: JSON.stringify({ confirmation }),
    });
  },
};

// ---------------------------------------------------------------------------
// Registrations (public, auth required)
// ---------------------------------------------------------------------------

export const registrationService = {
  register(slug: string, data: RegistrationFormData) {
    return apiFetch<RegistrationResponse>(`/events/activities/${slug}/register/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  cancel(slug: string) {
    return apiFetch<void>(`/events/activities/${slug}/cancel/`, { method: 'POST' });
  },

  myRegistrations(futureOnly?: boolean) {
    const qs = futureOnly !== undefined ? `?future_only=${futureOnly}` : '';
    return apiFetch<MyRegistration[]>(`/me/registrations/${qs}`);
  },
};

// ---------------------------------------------------------------------------
// Bookmarks (public, auth required)
// ---------------------------------------------------------------------------

export const bookmarkService = {
  add(slug: string) {
    return apiFetch<{ bookmarked: boolean }>(`/events/activities/${slug}/bookmark/`, {
      method: 'POST',
    });
  },

  remove(slug: string) {
    return apiFetch<void>(`/events/activities/${slug}/bookmark/`, { method: 'DELETE' });
  },

  myBookmarks() {
    return apiFetch<ActivityListItem[]>('/me/bookmarks/');
  },
};

// ---------------------------------------------------------------------------
// Tags (public)
// ---------------------------------------------------------------------------

export const tagsService = {
  list(params?: { is_main_tag?: boolean }) {
    const qs = params?.is_main_tag !== undefined ? `?is_main_tag=${params.is_main_tag}` : '';
    return apiFetch<PaginatedResponse<Tag>>(`/events/tags/${qs}`);
  },
};

// ---------------------------------------------------------------------------
// Partners (public)
// ---------------------------------------------------------------------------

export const partnersService = {
  list() {
    return apiFetch<PaginatedResponse<PartnerListItem>>('/partners/partners/?page_size=200');
  },

  detail(slug: string) {
    return apiFetch<PartnerDetail>(`/partners/partners/${slug}/`);
  },
};

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------

export interface ContactFormPayload {
  name: string;
  email: string;
  subject: string;
  message: string;
  captcha_token: string;
}

export const contactService = {
  send(payload: ContactFormPayload) {
    return apiFetch<{ detail: string }>('/contact/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

// ---------------------------------------------------------------------------
// Sponsors (public)
// ---------------------------------------------------------------------------

export const sponsorsService = {
  list() {
    return apiFetch<PaginatedResponse<Sponsor>>('/sponsors/?page_size=200');
  },
};

// ---------------------------------------------------------------------------
// Staff: Sponsors
// ---------------------------------------------------------------------------

export const staffSponsorsService = {
  list(params?: { page?: number; search?: string }) {
    return apiFetch<PaginatedResponse<StaffSponsor>>(buildStaffListUrl('/staff/sponsors/', params));
  },

  create(formData: FormData) {
    return apiUpload<StaffSponsor>('/staff/sponsors/', formData, 'POST');
  },

  update(id: number, data: Partial<StaffSponsor>) {
    return apiFetch<StaffSponsor>(`/staff/sponsors/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  updateWithFile(id: number, formData: FormData) {
    return apiUpload<StaffSponsor>(`/staff/sponsors/${id}/`, formData, 'PATCH');
  },

  delete(id: number) {
    return apiFetch<void>(`/staff/sponsors/${id}/`, { method: 'DELETE' });
  },
};

// ---------------------------------------------------------------------------
// Activities (public)
// ---------------------------------------------------------------------------

export const activitiesService = {
  list(params?: Record<string, string>) {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    return apiFetch<PaginatedResponse<ActivityListItem>>(`/events/activities/${qs}`);
  },

  listPage(fullUrl: string) {
    // For pagination — next/previous are full URLs
    const url = new URL(fullUrl);
    return apiFetch<PaginatedResponse<ActivityListItem>>(
      url.pathname.replace('/api/v1', '') + url.search
    );
  },

  detail(slug: string) {
    return apiFetch<ActivityDetail>(`/events/activities/${slug}/`);
  },
};

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------

export const calendarService = {
  getMonth(year: number, month: number) {
    return apiFetch<CalendarResponse>(`/calendar/?year=${year}&month=${month}`);
  },
};

// ---------------------------------------------------------------------------
// Staff: Tags
// ---------------------------------------------------------------------------

export const staffTagsService = {
  list(params?: { page?: number; search?: string }) {
    return apiFetch<PaginatedResponse<Tag>>(buildStaffListUrl('/staff/tags/', params));
  },

  create(data: { name: string; icon?: string; is_main_tag?: boolean; color?: string }) {
    return apiFetch<Tag>('/staff/tags/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(id: number, data: Partial<Tag>) {
    return apiFetch<Tag>(`/staff/tags/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete(id: number) {
    return apiFetch<void>(`/staff/tags/${id}/`, { method: 'DELETE' });
  },
};

// ---------------------------------------------------------------------------
// Staff: Partners
// ---------------------------------------------------------------------------

export const staffPartnersService = {
  list(params?: { page?: number; search?: string }) {
    return apiFetch<PaginatedResponse<PartnerDetail>>(buildStaffListUrl('/staff/partners/', params));
  },

  create(formData: FormData) {
    return apiUpload<PartnerDetail>('/staff/partners/', formData, 'POST');
  },

  update(id: number, data: Record<string, unknown>) {
    return apiFetch<PartnerDetail>(`/staff/partners/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  updateWithFile(id: number, formData: FormData) {
    return apiUpload<PartnerDetail>(`/staff/partners/${id}/`, formData, 'PATCH');
  },

  delete(id: number) {
    return apiFetch<void>(`/staff/partners/${id}/`, { method: 'DELETE' });
  },

  revokeDpa(id: number) {
    return apiFetch<{ detail: string; is_validated: boolean }>(`/staff/partner-dpa/${id}/revoke-dpa/`, {
      method: 'POST',
    });
  },
};

// ---------------------------------------------------------------------------
// Staff: Activities
// ---------------------------------------------------------------------------

export const staffActivitiesService = {
  list(params?: { page?: number; search?: string }) {
    return apiFetch<PaginatedResponse<StaffActivity>>(buildStaffListUrl('/staff/activities/', params));
  },

  detail(id: number | string) {
    return apiFetch<StaffActivity>(`/staff/activities/${id}/`);
  },

  create(data: Record<string, unknown>) {
    return apiFetch<StaffActivity>('/staff/activities/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(id: number | string, data: Record<string, unknown>) {
    return apiFetch<StaffActivity>(`/staff/activities/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  uploadBanner(id: number | string, file: File) {
    const formData = new FormData();
    formData.append('banner_image', file);
    return apiUpload<StaffActivity>(`/staff/activities/${id}/`, formData, 'PATCH');
  },

  delete(id: number | string) {
    return apiFetch<void>(`/staff/activities/${id}/`, { method: 'DELETE' });
  },
};

// ---------------------------------------------------------------------------
// Staff: Participants
// ---------------------------------------------------------------------------

export const staffParticipantsService = {
  list(slug: string, params?: { page?: number; search?: string; status?: string }) {
    return apiFetch<PaginatedResponse<Participant> & { stats?: ParticipantStats }>(buildStaffListUrl(`/staff/activities/${slug}/participants/`, params));
  },

  printUrl(slug: string) {
    return `/staff/activities/${slug}/participants/?output=print`;
  },

  cancel(slug: string, participantId: number) {
    return apiFetch<void>(`/staff/activities/${slug}/participants/${participantId}/cancel/`, {
      method: 'POST',
    });
  },
};

// ---------------------------------------------------------------------------
// Staff/Partner: Participants (partner view — cancelled redacted)
// ---------------------------------------------------------------------------

export const partnerParticipantsService = {
  list(slug: string, params?: { page?: number }) {
    return apiFetch<PaginatedResponse<Participant> & { stats?: ParticipantStats }>(buildStaffListUrl(`/staff/partner-activities/${slug}/participants/`, params));
  },

  printUrl(slug: string) {
    return `/staff/partner-activities/${slug}/participants/?output=print`;
  },
};

// ---------------------------------------------------------------------------
// Staff: Teams (IsSuperUser)
// ---------------------------------------------------------------------------

export const staffTeamsService = {
  list(params?: { page?: number }) {
    return apiFetch<PaginatedResponse<Team>>(buildStaffListUrl('/staff/teams/', params));
  },

  create(data: { name: string; color: string }) {
    return apiFetch<Team>('/staff/teams/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(id: number, data: Partial<Team>) {
    return apiFetch<Team>(`/staff/teams/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete(id: number) {
    return apiFetch<void>(`/staff/teams/${id}/`, { method: 'DELETE' });
  },
};

// ---------------------------------------------------------------------------
// Staff: Users (IsSuperUser)
// ---------------------------------------------------------------------------

export const staffUsersService = {
  list(params?: { page?: number; search?: string }) {
    return apiFetch<PaginatedResponse<StaffUser>>(buildStaffListUrl('/staff/users/', params));
  },

  detail(id: number) {
    return apiFetch<StaffUser>(`/staff/users/${id}/`);
  },

  create(data: Record<string, unknown>) {
    return apiFetch<StaffUser>('/staff/users/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(id: number, data: Record<string, unknown>) {
    return apiFetch<StaffUser>(`/staff/users/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete(id: number) {
    return apiFetch<void>(`/staff/users/${id}/`, { method: 'DELETE' });
  },

  resendInvite(id: number) {
    return apiFetch<{ detail: string }>(`/staff/users/${id}/resend-invite/`, { method: 'POST' });
  },

  importTemplateUrl() {
    return '/staff/users/import-template/';
  },

  bulkImport(csv: string) {
    return apiFetch<{
      created: string[];
      skipped: string[];
      errors: string[];
      created_count: number;
      skipped_count: number;
      error_count: number;
    }>('/staff/users/bulk-import/', {
      method: 'POST',
      body: JSON.stringify({ csv }),
    });
  },
};

// ---------------------------------------------------------------------------
// Staff/Partner: Partner Activities
// ---------------------------------------------------------------------------

export const partnerActivitiesService = {
  list(params?: { page?: number }) {
    return apiFetch<PaginatedResponse<StaffActivity>>(buildStaffListUrl('/staff/partner-activities/', params));
  },

  detail(id: number | string) {
    return apiFetch<StaffActivity>(`/staff/partner-activities/${id}/`);
  },

  create(data: Record<string, unknown>) {
    return apiFetch<StaffActivity>('/staff/partner-activities/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(id: number | string, data: Record<string, unknown>) {
    return apiFetch<StaffActivity>(`/staff/partner-activities/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

// ---------------------------------------------------------------------------
// Staff/Partner: My Partners (profile management)
// ---------------------------------------------------------------------------

export const myPartnersService = {
  list(params?: { page?: number }) {
    return apiFetch<PaginatedResponse<PartnerDetail>>(buildStaffListUrl('/staff/my-partners/', params));
  },

  update(id: number, data: Record<string, unknown>) {
    return apiFetch<PartnerDetail>(`/staff/my-partners/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  acceptDpa(id: number) {
    return apiFetch<{ detail: string; is_validated: boolean; dpa_accepted_at: string }>(`/staff/my-partners/${id}/accept-dpa/`, {
      method: 'POST',
    });
  },
};

// ---------------------------------------------------------------------------
// Staff: Invitations
// ---------------------------------------------------------------------------

export const staffInvitationsService = {
  list(slug: string, params?: { page?: number; status?: string }) {
    return apiFetch<PaginatedResponse<ActivityInvitation>>(
      buildStaffListUrl(`/staff/activities/${slug}/invitations/`, params)
    );
  },

  create(slug: string, data: { emails?: string[]; csv?: string }) {
    return apiFetch<{ created: string[]; skipped: string[]; created_count: number; skipped_count: number }>(
      `/staff/activities/${slug}/invitations/`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  },

  resend(slug: string, invitationId: number) {
    return apiFetch<{ detail: string }>(
      `/staff/activities/${slug}/invitations/${invitationId}/resend/`,
      { method: 'POST' },
    );
  },
};

// ---------------------------------------------------------------------------
// Public: Invitation response
// ---------------------------------------------------------------------------

export const invitationService = {
  getDetails(token: string) {
    return apiFetch<{ activity_title: string; activity_slug: string; email: string; status: string }>(
      `/events/invitation/${token}/`
    );
  },

  respond(token: string, data: { action: 'accept' | 'decline'; reason?: string }) {
    return apiFetch<{ status: string }>(`/events/invitation/${token}/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// ---------------------------------------------------------------------------
// Staff: Volunteer Teams
// ---------------------------------------------------------------------------

export const staffVolunteerTeamsService = {
  list(slug: string) {
    return apiFetch<PaginatedResponse<VolunteerTeam>>(buildStaffListUrl(`/staff/activities/${slug}/volunteer-teams/`));
  },

  create(slug: string, data: { name: string; description?: string; icon?: string; color?: string; capacity?: number | null }) {
    return apiFetch<VolunteerTeam>(`/staff/activities/${slug}/volunteer-teams/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(slug: string, id: number, data: Partial<VolunteerTeam>) {
    return apiFetch<VolunteerTeam>(`/staff/activities/${slug}/volunteer-teams/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete(slug: string, id: number) {
    return apiFetch<void>(`/staff/activities/${slug}/volunteer-teams/${id}/`, { method: 'DELETE' });
  },
};

// ---------------------------------------------------------------------------
// Staff: Volunteer Applications
// ---------------------------------------------------------------------------

export const staffVolunteersService = {
  list(slug: string, params?: { page?: number; status?: string; team?: string; search?: string }) {
    return apiFetch<PaginatedResponse<VolunteerApplication>>(
      buildStaffListUrl(`/staff/activities/${slug}/volunteers/`, params)
    );
  },

  assign(slug: string, data: { user_id: number; team_id?: number | null; motivation?: string; staff_notes?: string }) {
    return apiFetch<VolunteerApplication>(`/staff/activities/${slug}/volunteers/assign/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  review(slug: string, applicationId: number, data: { action: 'approve' | 'reject' | 'rescind' | 'revert_to_pending' | 'revert_to_approved'; staff_notes?: string }) {
    return apiFetch<VolunteerApplication>(`/staff/activities/${slug}/volunteers/${applicationId}/review/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(slug: string, applicationId: number, data: { staff_notes?: string; team?: number | null }) {
    return apiFetch<VolunteerApplication>(`/staff/activities/${slug}/volunteers/${applicationId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  stats(activitySlug?: string) {
    const qs = activitySlug ? `?activity=${activitySlug}` : '';
    return apiFetch<VolunteerStats>(`/staff/volunteer-stats/${qs}`);
  },
};

// ---------------------------------------------------------------------------
// Staff: Coordinators
// ---------------------------------------------------------------------------

export const staffCoordinatorsService = {
  list(slug: string) {
    return apiFetch<PaginatedResponse<Coordinator>>(buildStaffListUrl(`/staff/activities/${slug}/coordinators/`));
  },

  add(slug: string, userId: number) {
    return apiFetch<Coordinator>(`/staff/activities/${slug}/coordinators/`, {
      method: 'POST',
      body: JSON.stringify({ user: userId }),
    });
  },

  remove(slug: string, coordinatorId: number) {
    return apiFetch<void>(`/staff/activities/${slug}/coordinators/${coordinatorId}/`, { method: 'DELETE' });
  },
};

// ---------------------------------------------------------------------------
// Staff: Volunteer Role Toggle
// ---------------------------------------------------------------------------

export const staffVolunteerRoleService = {
  toggle(userId: number) {
    return apiFetch<{ user_id: number; is_volunteer: boolean }>(`/staff/users/${userId}/toggle-volunteer/`, {
      method: 'POST',
    });
  },

  reviewRequest(userId: number, data: { action: 'approve' | 'reject'; reason?: string }) {
    return apiFetch<{ user_id: number; is_volunteer: boolean; request_status: string }>(`/staff/users/${userId}/volunteer-request/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// ---------------------------------------------------------------------------
// Public: Volunteer browsing & My applications
// ---------------------------------------------------------------------------

export const volunteerService = {
  openForVolunteers() {
    return apiFetch<ActivityListItem[]>('/events/open-for-volunteers/');
  },

  myApplications() {
    return apiFetch<PaginatedResponse<MyVolunteerApplication>>('/me/volunteer-applications/');
  },

  apply(slug: string, data?: { motivation?: string; team_id?: number | null; visibility_consent?: boolean }) {
    return apiFetch<MyVolunteerApplication>(`/events/activities/${slug}/volunteer/`, {
      method: 'POST',
      body: JSON.stringify({
        motivation: data?.motivation || '',
        team_id: data?.team_id || null,
        visibility_consent: data?.visibility_consent || false,
      }),
    });
  },

  withdraw(slug: string) {
    return apiFetch<void>(`/events/activities/${slug}/volunteer/`, { method: 'DELETE' });
  },

  confirmedVolunteers(slug: string) {
    return apiFetch<{
      coordinators: { first_name: string; last_name: string }[];
      teams: { team_name: string | null; volunteers: { first_name: string; last_name: string; source_organization: string }[] }[];
    }>(`/events/activities/${slug}/confirmed-volunteers/`);
  },

  activityTeams(slug: string) {
    return apiFetch<{ id: number; name: string; icon: string; color: string; capacity: number | null; confirmed_count: number }[]>(
      `/events/activities/${slug}/volunteer-teams/`
    );
  },
};

// ---------------------------------------------------------------------------
// Staff: Audit Log (IsSuperUser)
// ---------------------------------------------------------------------------

export const staffAuditLogService = {
  list(params?: { page?: number; action?: string; activity_id?: string }) {
    return apiFetch<PaginatedResponse<AuditLogEntry>>(buildStaffListUrl('/staff/audit-log/', params));
  },
};
